import { Pagination, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';

type Props<T extends { _id?: string }> = {
  pageParamName: string;
  list: T[];
};

const DEFAULT_PAGE_SIZE = 50;

const useUIPagination = <T extends { _id?: string }>({ pageParamName, list }: Props<T>) => {
  const router = useRouter();

  const pageParam = router.query[pageParamName];
  const parsedPage = pageParam ? parseInt(pageParam as string, 10) : 1;
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const start = (page - 1) * DEFAULT_PAGE_SIZE;
  const paginatedList = list.slice(start, start + DEFAULT_PAGE_SIZE);

  const handlePageChange = (page: number) => {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, [pageParamName]: page.toString() },
      },
      undefined,
      { shallow: true },
    );
  };

  const startIndex = (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const endIndex = Math.min(page * DEFAULT_PAGE_SIZE, list.length);

  const renderPagination = () => {
    if (list.length <= DEFAULT_PAGE_SIZE) {
      return null;
    }
    return (
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="flex-start"
        direction="row"
        sx={{ marginTop: '20px' }}
      >
        <Pagination
          shape="rounded"
          variant="outlined"
          color="primary"
          count={Math.ceil(list.length / DEFAULT_PAGE_SIZE)}
          page={page}
          onChange={(_e, value) => handlePageChange(value)}
          sx={{ display: 'flex', alignItems: 'flex-start' }}
        />

        {list.length > 0 && (
          <Typography
            variant="body1"
            sx={{
              padding: '0 10px',
              margin: '0 10px',
              fontWeight: '500',
            }}
          >
            Results: {startIndex}-{endIndex} of {list.length}
          </Typography>
        )}
      </Stack>
    );
  };

  return {
    paginatedList,
    renderPagination,
  };
};

export default useUIPagination;
